import React, { useState } from 'react';

import { PlusOutlined, LoadingOutlined } from '@ant-design/icons';
import { Upload, message } from 'antd';
import styled from 'styled-components';

import { isValidHexColor } from '../../../utils/isValidHexColor/isValidHexColor';
import {
  renderWhiteLabelImage,
  type WhiteLabelImageKind,
} from '../../../utils/whiteLabelImage/whiteLabelImage';
import { FormWrapper } from './styled';

const ACCEPTED_MIME_TYPES = ['image/jpeg', 'image/png'];
const MAX_FILE_SIZE_MB = 10;

const NON_HEX_DIGIT_REGEX = /[^0-9a-fA-F]/g;

// Aceita tanto os 6 dígitos digitados soltos quanto um valor colado já com
// "#" na frente — em ambos os casos sobra só os dígitos hex, até 6.
const sanitizeHexDigits = (raw: string) =>
  raw.replace(/#/g, '').replace(NON_HEX_DIGIT_REGEX, '').slice(0, 6);

const hexDigitsToColor = (digits: string) => (digits ? `#${digits}` : '');

export interface DashCustomizationValue {
  primary_color: string;
  secondary_color: string;
  logo_base64: string;
  favicon_base64: string;
  tenant_alias_image_base64: string;
}

export const EMPTY_DASH_CUSTOMIZATION: DashCustomizationValue = {
  primary_color: '',
  secondary_color: '',
  logo_base64: '',
  favicon_base64: '',
  tenant_alias_image_base64: '',
};

// layout próprio da seção — não reaproveita Field/FormGrid (feitos pro form
// bancário) pra evitar que a regra `input { width: 100% }` deles estoure o
// tamanho do checkbox "usar mesma cor".

const ColorsRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px 20px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const ColorField = styled.div`
  display: flex;
  flex-direction: column;

  label {
    font-size: 14px;
    color: #9ca3af;
    margin-bottom: 6px;
  }
`;

const ColorInputRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;

  input[type='color'] {
    flex-shrink: 0;
    width: 40px;
    height: 40px;
    padding: 0;
    border: none;
    background: none;
  }
`;

const HexInputWrapper = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
  box-sizing: border-box;
  padding: 0 12px;
  border: 1px solid #d0d5dd;
  border-radius: 6px;

  &:focus-within {
    border-color: ${({ theme }) => theme.primary};
  }

  span {
    color: #9ca3af;
    font-size: 15px;
  }

  input {
    flex: 1;
    min-width: 0;
    border: none;
    outline: none;
    background: transparent;
    padding: 10px 0 10px 2px;
    font-size: 15px;

    &:disabled {
      color: #9ca3af;
    }
  }
`;

const UseSameColorLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  font-size: 14px;
  font-weight: normal;
  color: #374151;
  cursor: pointer;

  input[type='checkbox'] {
    width: 16px;
    height: 16px;
    margin: 0;
    padding: 0;
    border: none;
  }
`;

const ColorError = styled.span`
  display: block;
  color: #dc2626;
  font-size: 12px;
  margin-top: 4px;
`;

const ImagesRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px 20px;
  margin-top: 24px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const ImageField = styled.div`
  display: flex;
  flex-direction: column;

  label {
    font-size: 14px;
    color: #9ca3af;
    margin-bottom: 6px;
  }
`;

const ImageSizeHint = styled.span`
  margin-top: 6px;
  font-size: 12px;
  color: #9ca3af;
`;

interface ImageUploadFieldProps {
  label: string;
  kind: WhiteLabelImageKind;
  sizeHint: string;
  base64: string;
  onChange: (base64: string) => void;
}

function ImageUploadField({ label, kind, sizeHint, base64, onChange }: ImageUploadFieldProps) {
  const [loading, setLoading] = useState(false);

  const processFile = async (file: File) => {
    setLoading(true);
    // O arquivo vai pro <img> por URL de objeto em vez de ser lido inteiro pelo
    // FileReader: menos uma etapa assíncrona entre escolher e ver a imagem.
    const objectUrl = URL.createObjectURL(file);
    try {
      // Já sai no tamanho final do white-label (PNG): o payload fica leve e
      // o backend só grava o arquivo.
      onChange(await renderWhiteLabelImage(objectUrl, kind));
    } catch {
      message.error('Não foi possível processar a imagem');
    } finally {
      URL.revokeObjectURL(objectUrl);
      setLoading(false);
    }
  };

  // beforeUpload é síncrono e sempre devolve false: o Upload nunca entra no
  // ciclo de envio do antd, e a conversão roda à parte. Antes, com o fileList
  // controlado pelo base64, o re-render do "uploading" apagava o item da lista
  // interna antes do onSuccess chegar e o evento "done" era engolido — a
  // imagem só pegava na segunda tentativa.
  const beforeUpload = (file: File) => {
    const isAcceptedType = ACCEPTED_MIME_TYPES.includes(file.type);
    if (!isAcceptedType) {
      message.error('Envie um arquivo .jpg, .jpeg ou .png');
      return false;
    }

    const isWithinSizeLimit = file.size / 1024 / 1024 < MAX_FILE_SIZE_MB;
    if (!isWithinSizeLimit) {
      message.error(`A imagem deve ter menos de ${MAX_FILE_SIZE_MB}MB`);
      return false;
    }

    void processFile(file);

    return false;
  };

  const onRemove = () => {
    onChange('');
  };

  return (
    <ImageField>
      <label>{label}</label>
      <Upload
        listType='picture-card'
        fileList={
          (base64
            ? [{ uid: label, name: label, status: 'done', url: base64 }]
            : []) as any
        }
        beforeUpload={beforeUpload}
        onRemove={onRemove}
        accept='image/png, image/jpeg'
      >
        {base64 ? null : (
          <div>
            {loading ? <LoadingOutlined /> : <PlusOutlined />}
            <div style={{ marginTop: 8 }}>Upload</div>
          </div>
        )}
      </Upload>
      <ImageSizeHint>{sizeHint}</ImageSizeHint>
    </ImageField>
  );
}

interface DashCustomizationSectionProps {
  value: DashCustomizationValue;
  // Dispatch do useState do pai: as imagens são processadas de forma assíncrona
  // e precisam atualizar a partir do estado mais recente (o `value` do render
  // em que o arquivo foi escolhido pode estar velho quando a conversão termina).
  onChange: React.Dispatch<React.SetStateAction<DashCustomizationValue>>;
  useSameColor: boolean;
  onUseSameColorChange: (value: boolean) => void;
}

export default function DashCustomizationSection({
  value,
  onChange,
  useSameColor,
  onUseSameColorChange,
}: DashCustomizationSectionProps) {
  const setPrimaryColor = (v: string) => {
    onChange({
      ...value,
      primary_color: v,
      secondary_color: useSameColor ? v : value.secondary_color,
    });
  };

  const setSecondaryColor = (v: string) => {
    onChange({ ...value, secondary_color: v });
  };

  const toggleUseSameColor = (checked: boolean) => {
    onUseSameColorChange(checked);
    onChange({
      ...value,
      secondary_color: checked ? value.primary_color : value.secondary_color,
    });
  };

  const primaryColorError =
    !!value.primary_color && !isValidHexColor(value.primary_color);
  const secondaryColorError =
    !useSameColor &&
    !!value.secondary_color &&
    !isValidHexColor(value.secondary_color);

  return (
    <FormWrapper>
      <ColorsRow>
        <ColorField>
          <label>Cor primária</label>
          <ColorInputRow>
            <input
              type='color'
              value={isValidHexColor(value.primary_color) ? value.primary_color : '#000000'}
              onChange={(e) => setPrimaryColor(e.target.value)}
            />
            <HexInputWrapper>
              <span>#</span>
              <input
                type='text'
                placeholder='f38a54'
                maxLength={6}
                value={value.primary_color.replace(/^#/, '')}
                onChange={(e) =>
                  setPrimaryColor(hexDigitsToColor(sanitizeHexDigits(e.target.value)))
                }
              />
            </HexInputWrapper>
          </ColorInputRow>
          {primaryColorError && (
            <ColorError>Informe uma cor hex válida (ex: #f38a54)</ColorError>
          )}
        </ColorField>

        <ColorField>
          <label>Cor secundária</label>
          <ColorInputRow>
            <input
              type='color'
              disabled={useSameColor}
              value={isValidHexColor(value.secondary_color) ? value.secondary_color : '#000000'}
              onChange={(e) => setSecondaryColor(e.target.value)}
            />
            <HexInputWrapper>
              <span>#</span>
              <input
                type='text'
                placeholder='f38a54'
                maxLength={6}
                disabled={useSameColor}
                value={value.secondary_color.replace(/^#/, '')}
                onChange={(e) =>
                  setSecondaryColor(hexDigitsToColor(sanitizeHexDigits(e.target.value)))
                }
              />
            </HexInputWrapper>
          </ColorInputRow>
          <UseSameColorLabel>
            <input
              type='checkbox'
              checked={useSameColor}
              onChange={(e) => toggleUseSameColor(e.target.checked)}
            />
            Usar mesma cor primária
          </UseSameColorLabel>
          {secondaryColorError && (
            <ColorError>Informe uma cor hex válida (ex: #f38a54)</ColorError>
          )}
        </ColorField>
      </ColorsRow>

      <ImagesRow>
        <ImageUploadField
          label='Logo'
          kind='logo'
          sizeHint='Ajustada para 300x100'
          base64={value.logo_base64}
          onChange={(base64) =>
            onChange((prev) => ({ ...prev, logo_base64: base64 }))
          }
        />
        <ImageUploadField
          label='Favicon'
          kind='favicon'
          sizeHint='Ajustado para 64x64'
          base64={value.favicon_base64}
          onChange={(base64) =>
            onChange((prev) => ({ ...prev, favicon_base64: base64 }))
          }
        />
        <ImageUploadField
          label='Ícone do tenant (tenant-alias)'
          kind='tenantAlias'
          sizeHint='Ajustado para 192x192, redondo'
          base64={value.tenant_alias_image_base64}
          onChange={(base64) =>
            onChange((prev) => ({ ...prev, tenant_alias_image_base64: base64 }))
          }
        />
      </ImagesRow>
    </FormWrapper>
  );
}
